// This component was taken from
// https://gitlab.internal.equipmentshare.com/pim/pim-ui/-/blob/main/src/components/common/search-input.tsx
import React, { useEffect, useRef, useState, type KeyboardEvent } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import {
	Button,
	TextField,
	IconButton,
	InputAdornment,
	CircularProgress,
	type BaseTextFieldProps,
} from '@mui/material'

const StartAdornment = () => {
	return (
		<InputAdornment position="start" sx={{ ml: -0.5, mr: 0.5 }}>
			<SearchIcon sx={{ width: 16, height: 16 }} />
		</InputAdornment>
	)
}

type EndAdornmentProps = {
  isLoading?: boolean,
  hasValue?: boolean,
  onApply: () => void,
  onClear: () => void,
  showSearchIcon?: boolean,
};

const EndAdornment = ( {
	isLoading,
	hasValue,
	onApply,
	onClear,
	showSearchIcon,
}: EndAdornmentProps ) => {
	if ( isLoading ) {
		return <CircularProgress size={16} sx={{ mr: 1 }} />
	}

	return (
		<>
			{hasValue && (
				<IconButton size="small" onClick={onClear} data-testid="clear-search">
					<CloseIcon sx={{ width: 16, height: 16 }} />
				</IconButton>
			)}
			{showSearchIcon && (
				<Button
					size="small"
					onClick={onApply}
					sx={{ minWidth: 12, padding: 0.5 }}
					color={hasValue ? 'primary' : 'inherit'}
					variant={hasValue ? 'contained' : 'text'}
				>
					<SearchIcon sx={{ width: 16, height: 16 }} />
				</Button>
			)}
		</>
	)
}

export type SearchInputProps = {
  value?: string,
  autoApply?: boolean,
  placeholder?: string,
  onClear?: () => void,
  autoApplyDelay?: number,
  onChange?: ( value: string ) => void,
  onApply?: ( value: string ) => void,
} & BaseTextFieldProps;

export const SearchInput = ( {
	sx,
	value,
	onApply,
	onClear,
	onChange,
	autoApply,
	autoApplyDelay = 300,
	placeholder = 'Search',
	...rest
}: SearchInputProps ) => {
	const [ internalValue, setInteralValue ] = useState( value ?? '' )
	const [ isDebouncing, setIsDebouncing ] = useState( false )
	const applyTimeoutRef = useRef<ReturnType<typeof setTimeout>>()

	useEffect( () => {
		if ( value === internalValue ) {
			return
		}
		if ( autoApply && onApply ) {
			setIsDebouncing( true )
			applyTimeoutRef.current = setTimeout( () => {
				onApply( internalValue )
				setIsDebouncing( false )
			}, autoApplyDelay )
		}
		if ( onChange ) {
			onChange( internalValue )
		}

		return () => {
			clearTimeout( applyTimeoutRef.current )
		}
	}, [ autoApply, autoApplyDelay, internalValue, onApply, onChange, value ] )

	const handleApply = () => {
		setIsDebouncing( false )
		clearTimeout( applyTimeoutRef.current )
		if ( onApply ) {
			onApply( internalValue )
		}
	}

	const handleKeyDown = ( e: KeyboardEvent<HTMLDivElement> ) => {
		if ( e.key === 'Enter' ) {
			handleApply()
		}
	}

	const handleClear = () => {
		setInteralValue( '' )
		clearTimeout( applyTimeoutRef.current )
		if ( onClear ) {
			onClear()
		}
	}

	return (
		<TextField
			size="small"
			value={internalValue}
			placeholder={placeholder}
			onKeyDown={handleKeyDown}
			onChange={( e ) => setInteralValue( e.target.value )}
			sx={{
				'& .MuiInputBase-input': { height: 15 },
				'& .MuiInputBase-root': {
					'pr': 0.5,
					'& > .MuiIconButton-root': {
						visibility: 'hidden',
					},
					'&:focus-within > .MuiIconButton-root': {
						visibility: 'visible',
					},
					'&:not(:focus-within) .MuiIconButton-colorPrimary': {
						color: 'inherit',
					},
				},
				...sx,
			}}
			slotProps={{
				inputLabel: {
					shrink: true,
				},
				input: {
					autoComplete: 'off',
					startAdornment: autoApply ? <StartAdornment /> : null,
					endAdornment: (
						<InputAdornment position="end">
							<EndAdornment
								onClear={handleClear}
								onApply={handleApply}
								isLoading={isDebouncing}
								hasValue={!!internalValue}
								showSearchIcon={!autoApply}
							/>
						</InputAdornment>
					),
				},
			}}
			{...rest}
		/>
	)
}
